import { useState } from 'react';
import { useSiteData } from '@/contexts/SiteDataContext';
import type { HeroSlide } from '@/contexts/SiteDataContext';

const emptySlide: HeroSlide = { url: '', alt: '', caption: '', tag: '' };

export default function HeroSlidesSection() {
  const { data, updateHeroSlides } = useSiteData();
  const [slides, setSlides] = useState<HeroSlide[]>([...data.heroSlides]);
  const [saved, setSaved] = useState(false);

  const handleSave = () => {
    updateHeroSlides(slides);
    setSaved(true);
    setTimeout(() => setSaved(false), 2500);
  };

  const updateSlide = (i: number, field: keyof HeroSlide, value: string) => {
    const updated = slides.map((s, idx) => idx === i ? { ...s, [field]: value } : s);
    setSlides(updated);
  };

  const addSlide = () => setSlides([...slides, { ...emptySlide }]);
  const removeSlide = (i: number) => setSlides(slides.filter((_, idx) => idx !== i));
  const moveUp = (i: number) => {
    if (i === 0) return;
    const s = [...slides];
    [s[i - 1], s[i]] = [s[i], s[i - 1]];
    setSlides(s);
  };
  const moveDown = (i: number) => {
    if (i === slides.length - 1) return;
    const s = [...slides];
    [s[i], s[i + 1]] = [s[i + 1], s[i]];
    setSlides(s);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-xl font-bold text-foreground-900 mb-1">Hero Slideshow</h2>
          <p className="text-foreground-500 text-sm">Edit, reorder, add or remove slides on the homepage hero.</p>
        </div>
        <button
          onClick={addSlide}
          className="inline-flex items-center gap-2 px-4 py-2.5 bg-secondary-500 text-white rounded-xl text-sm font-semibold hover:bg-secondary-600 transition-colors whitespace-nowrap"
        >
          <i className="ri-add-line"></i> Add Slide
        </button>
      </div>

      <div className="space-y-5">
        {slides.map((slide, i) => (
          <div key={i} className="border border-background-200 rounded-2xl overflow-hidden">
            <div className="flex items-center justify-between px-4 py-3 bg-background-100 border-b border-background-200">
              <div className="flex items-center gap-2">
                {slide.url && (
                  <img src={slide.url} alt="" className="w-10 h-10 rounded-lg object-cover" />
                )}
                <span className="text-sm font-bold text-foreground-800">Slide {i + 1}</span>
              </div>
              <div className="flex items-center gap-2">
                <button onClick={() => moveUp(i)} disabled={i === 0} className="w-7 h-7 flex items-center justify-center rounded-lg bg-background-200 hover:bg-background-300 disabled:opacity-30 transition-colors cursor-pointer">
                  <i className="ri-arrow-up-line text-xs text-foreground-600"></i>
                </button>
                <button onClick={() => moveDown(i)} disabled={i === slides.length - 1} className="w-7 h-7 flex items-center justify-center rounded-lg bg-background-200 hover:bg-background-300 disabled:opacity-30 transition-colors cursor-pointer">
                  <i className="ri-arrow-down-line text-xs text-foreground-600"></i>
                </button>
                <button
                  onClick={() => removeSlide(i)}
                  className="w-7 h-7 flex items-center justify-center rounded-lg bg-rose-100 hover:bg-rose-200 text-rose-600 transition-colors cursor-pointer"
                >
                  <i className="ri-delete-bin-line text-xs"></i>
                </button>
              </div>
            </div>
            <div className="p-4 grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <label className="block text-xs font-semibold text-foreground-600 mb-1">Image URL</label>
                <input
                  type="text"
                  value={slide.url}
                  onChange={(e) => updateSlide(i, 'url', e.target.value)}
                  placeholder="https://..."
                  className="w-full px-3 py-2.5 rounded-xl border border-background-200 bg-background-50 text-foreground-900 text-sm focus:outline-none focus:ring-2 focus:ring-primary-300"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-foreground-600 mb-1">Caption (Headline)</label>
                <input
                  type="text"
                  value={slide.caption}
                  onChange={(e) => updateSlide(i, 'caption', e.target.value)}
                  className="w-full px-3 py-2.5 rounded-xl border border-background-200 bg-background-50 text-foreground-900 text-sm focus:outline-none focus:ring-2 focus:ring-primary-300"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-foreground-600 mb-1">Tag / Badge Text</label>
                <input
                  type="text"
                  value={slide.tag}
                  onChange={(e) => updateSlide(i, 'tag', e.target.value)}
                  className="w-full px-3 py-2.5 rounded-xl border border-background-200 bg-background-50 text-foreground-900 text-sm focus:outline-none focus:ring-2 focus:ring-primary-300"
                />
              </div>
              <div className="md:col-span-2">
                <label className="block text-xs font-semibold text-foreground-600 mb-1">Alt Text (accessibility)</label>
                <input
                  type="text"
                  value={slide.alt}
                  onChange={(e) => updateSlide(i, 'alt', e.target.value)}
                  className="w-full px-3 py-2.5 rounded-xl border border-background-200 bg-background-50 text-foreground-900 text-sm focus:outline-none focus:ring-2 focus:ring-primary-300"
                />
              </div>
            </div>
          </div>
        ))}
      </div>

      <button
        onClick={handleSave}
        className={`inline-flex items-center gap-2 px-6 py-3 rounded-xl font-semibold text-sm transition-all duration-300 whitespace-nowrap ${
          saved ? 'bg-emerald-500 text-white' : 'bg-primary-500 text-white hover:bg-primary-600'
        }`}
      >
        <i className={saved ? 'ri-check-line' : 'ri-save-line'}></i>
        {saved ? 'Saved!' : 'Save Hero Slides'}
      </button>
    </div>
  );
}